/*
  Cos Toast — sistem global de notificări.

  Triggere:
  1. CustomEvent pe document: document.dispatchEvent(new CustomEvent('cos:toast', { detail: {...} }))
  2. API global: window.CosToast.show({...}), .success(msg), .error(msg), .info(msg), .warning(msg)
  3. Server-side flash: <div hidden data-cos-toast-flash data-type="success" data-message="..." data-title="...">
     undeva în body (toast-stack le citește la init și le consumă; suportă mai multe simultan).

  Payload-ul `detail`:
    type:     'success' | 'error' | 'info' | 'warning'  (default: 'info')
    message:  string (obligatoriu)
    title:    string (opțional)
    duration: ms (default: 4000 success/info, 6000 error/warning); 0 = persistent
    icon:     nume Lucide override (opțional)
    action:   { label: string, onClick: () => void }  (opțional)
*/

import { loadCss } from 'utils';

const ICONS = {
  success: '<svg focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>',
  error:   '<svg focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>',
  info:    '<svg focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>',
  warning: '<svg focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>'
};

const CLOSE_ICON = '<svg focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

const DEFAULT_DURATION = { success: 4000, info: 4000, error: 6000, warning: 6000 };
const MAX_VISIBLE = 3;
const SWIPE_THRESHOLD = 60;
const ANIMATION_MS = 240;

let toastUid = 0;

class CosToast extends HTMLElement {
  constructor() {
    super();
    this._dismissed = false;
    this._timer = null;
    this._remaining = 0;
    this._lastTick = 0;
    this._drag = null;
  }

  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    this._render();
    this._bind();
    requestAnimationFrame(() => {
      this.dataset.state = 'enter';
      this._startTimer();
    });
  }

  disconnectedCallback() {
    this._clearTimer();
  }

  _render() {
    const type = this.dataset.type || 'info';
    const title = this.getAttribute('data-title');
    const message = this.getAttribute('data-message') || '';
    const closeLabel = (window.languages && window.languages.accessibilityClose) || 'Close';
    const actionLabel = this.getAttribute('data-action-label');

    this.setAttribute('role', (type === 'error' || type === 'warning') ? 'alert' : 'status');
    this.setAttribute('aria-live', (type === 'error' || type === 'warning') ? 'assertive' : 'polite');
    this.setAttribute('aria-atomic', 'true');

    const titleHtml = title ? `<strong class="cos-toast__title">${escape(title)}</strong>` : '';
    const messageHtml = message ? `<span class="cos-toast__message">${escape(message)}</span>` : '';
    const actionHtml = actionLabel
      ? `<button type="button" class="cos-toast__action" data-action="custom">${escape(actionLabel)}</button>`
      : '';
    const icon = ICONS[type] || ICONS.info;

    this.innerHTML = `
      <span class="cos-toast__icon">${icon}</span>
      <div class="cos-toast__body">${titleHtml}${messageHtml}</div>
      ${actionHtml}
      <button type="button" class="cos-toast__close" aria-label="${escape(closeLabel)}" data-action="close">${CLOSE_ICON}</button>
    `;
  }

  _bind() {
    this.addEventListener('click', (e) => {
      const action = e.target.closest('[data-action]')?.dataset.action;
      if (action === 'close') {
        this.dismiss();
      } else if (action === 'custom' && this._onAction) {
        this._onAction();
        this.dismiss();
      }
    });

    this.addEventListener('mouseenter', () => this._pauseTimer());
    this.addEventListener('mouseleave', () => this._resumeTimer());

    this.addEventListener('touchstart', this._onTouchStart.bind(this), { passive: true });
    this.addEventListener('touchmove', this._onTouchMove.bind(this), { passive: true });
    this.addEventListener('touchend', this._onTouchEnd.bind(this), { passive: true });
  }

  _startTimer() {
    const duration = parseInt(this.dataset.duration, 10);
    if (!duration || duration <= 0) return;
    this._remaining = duration;
    this._tick();
  }

  _tick() {
    this._lastTick = Date.now();
    this._timer = setTimeout(() => this.dismiss(), this._remaining);
  }

  _pauseTimer() {
    if (!this._timer) return;
    clearTimeout(this._timer);
    this._timer = null;
    this._remaining -= Date.now() - this._lastTick;
  }

  _resumeTimer() {
    if (this._timer || this._remaining <= 0) return;
    this._tick();
  }

  _clearTimer() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }

  _onTouchStart(e) {
    if (!e.touches[0]) return;
    this._drag = { startY: e.touches[0].clientY, deltaY: 0 };
    this.dataset.dragging = 'true';
    this._pauseTimer();
  }

  _onTouchMove(e) {
    if (!this._drag || !e.touches[0]) return;
    const dy = e.touches[0].clientY - this._drag.startY;
    if (dy < 0) return;
    this._drag.deltaY = dy;
    this.style.transform = `translateY(${dy}px) scale(${1 - Math.min(dy / 400, 0.04)})`;
    this.style.opacity = String(Math.max(1 - dy / 200, 0.3));
  }

  _onTouchEnd() {
    if (!this._drag) return;
    const dragged = this._drag.deltaY;
    this._drag = null;
    delete this.dataset.dragging;

    if (dragged > SWIPE_THRESHOLD) {
      this.dismiss();
    } else {
      this.style.transform = '';
      this.style.opacity = '';
      this._resumeTimer();
    }
  }

  dismiss() {
    if (this._dismissed) return;
    this._dismissed = true;
    this._clearTimer();
    this.dataset.state = 'leave';
    this.style.transform = '';
    this.style.opacity = '';
    setTimeout(() => this.remove(), ANIMATION_MS);
  }
}

class CosToastStack extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    document.addEventListener('cos:toast', this._onEvent.bind(this));
    this._consumeFlash();
  }

  _onEvent(e) {
    this.show(e.detail || {});
  }

  show(detail = {}) {
    const type = ['success', 'error', 'info', 'warning'].includes(detail.type) ? detail.type : 'info';
    const message = detail.message;
    if (!message) return null;

    const toast = document.createElement('cos-toast');
    toast.dataset.type = type;
    toast.dataset.uid = String(++toastUid);
    toast.setAttribute('data-message', message);
    if (detail.title) toast.setAttribute('data-title', detail.title);
    const duration = detail.duration != null ? detail.duration : DEFAULT_DURATION[type];
    toast.dataset.duration = String(duration);

    if (detail.action && detail.action.label) {
      toast.setAttribute('data-action-label', detail.action.label);
      toast._onAction = detail.action.onClick;
    }

    loadCss('cos-toast').then(() => {
      const existing = this.querySelectorAll('cos-toast');
      if (existing.length >= MAX_VISIBLE) {
        existing[0].dismiss();
      }
      this.appendChild(toast);
    });
    return toast;
  }

  _consumeFlash() {
    const nodes = document.querySelectorAll('[data-cos-toast-flash]');
    if (!nodes.length) return;
    loadCss('cos-toast');
    const flashes = [];
    nodes.forEach((node) => {
      flashes.push({
        type: node.dataset.type || 'info',
        message: node.dataset.message,
        title: node.dataset.title || undefined,
        duration: node.dataset.duration ? parseInt(node.dataset.duration, 10) : undefined
      });
      node.remove();
    });
    requestAnimationFrame(() => {
      flashes.forEach((detail) => {
        if (detail.message) this.show(detail);
      });
    });
  }
}

function escape(str) {
  return String(str).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));
}

if (!window.customElements.get('cos-toast')) {
  window.customElements.define('cos-toast', CosToast);
}
if (!window.customElements.get('cos-toast-stack')) {
  window.customElements.define('cos-toast-stack', CosToastStack);
}

function ensureStack() {
  let stack = document.querySelector('cos-toast-stack');
  if (!stack) {
    stack = document.createElement('cos-toast-stack');
    document.body.appendChild(stack);
  }
  return stack;
}

window.CosToast = {
  show(detail) {
    document.dispatchEvent(new CustomEvent('cos:toast', { detail }));
    return ensureStack();
  },
  success(message, opts = {}) {
    return this.show({ ...opts, type: 'success', message });
  },
  error(message, opts = {}) {
    return this.show({ ...opts, type: 'error', message });
  },
  info(message, opts = {}) {
    return this.show({ ...opts, type: 'info', message });
  },
  warning(message, opts = {}) {
    return this.show({ ...opts, type: 'warning', message });
  }
};
